import React from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux'
import { format, sub } from 'date-fns';

import { Button, Spinner, Tag, Modal } from 'components'
import { DeleteDialogue, BetaModal } from 'features';
import { FaCog, FaCheck, FaBolt, FaCalendarCheck, FaFileAlt, FaClipboardCheck, FaPeopleCarry } from 'react-icons/fa';

import * as api from 'api';
import * as actionCreators from 'store/actions';
import { userContext } from 'context';

const confirmButtonStyle = {
    backgroundColor: 'var(--focus-color)',
    // margin: '0 0 0 1rem',
    margin: 0,
    fontWeight: 'bold',
    color: 'white',
}

const cancelButtonStyle = {
    backgroundColor: 'var(--primary-color)',
    // margin: '0 0 0 1rem',
    margin: 0
}

const Container = styled.div`
    background-color: var(--secondary-color);
    border-radius: 5px;
    box-shadow: var(--box-shadow);
    padding: 2rem;
    display: grid;
    grid-template-columns: auto;
    grid-template-rows: auto auto;
    grid-row-gap: 1rem;
`

const Header = styled.div`
    display: grid;
    grid-template-columns: 1fr min-content;
`

const Wrapper = styled.div`
    display: grid;
    grid-template-columns: 47.5% 47.5%;
    grid-column-gap: 5%;
`

const Body = styled.div`
    /* display: grid;
    grid-template-columns: 50% 50%;
    grid-template-rows: auto auto auto; */
    display: flex;
    flex-direction: column;
`

const SelectionContainer = styled.div`
    display: grid;
    /* grid-template-columns: 47.5% 47.5%;
    grid-column-gap: 5%; */
    grid-template-rows: min-content min-content min-content min-content;
    grid-row-gap: 0.5rem;
`

const SelectionTile = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;

    height: fit-content;
    background-color: var(--secondary-color);
    border-radius: 5px;
    border-width: 2px;
    border-style: solid;
    border-color: ${props => props.selected ? 'var(--focus-color)' : 'var(--secondary-accent-color)'};
    padding: 1rem;
    cursor: ${props => props.selectable && 'pointer'};
    :hover{
        filter: ${props => props.selectable && 'brightness(var(--brightness))'};
    }
`

const Title = styled.div`
    font-weight: bold;
    font-size: 1rem;
`

const Subtitle = styled.div`
    /* font-weight: bold; */
    font-size: 0.9rem;
    color: ${props => props.styles && props.styles.color};
`

const FeatureTile = styled.div`
    display: grid;
    grid-template-columns: 1rem 1fr;
    grid-template-rows: min-content 1fr;
    margin: 0.5rem 1rem 0.5rem 0;
    align-items: start;
    grid-row-gap: 0.5rem;
    grid-column-gap: 0.5rem;
    /* border: 1px solid red; */
`

const Icon = styled.div`
    align-self: center;
    justify-self: center;
    height: 1rem;
    width: 1rem;
`

const Error = styled.div`
    font-size: 0.9rem;
    color: var(--error-color);
    margin-top: 0.2rem;
    :before {
        content: '*'
    }
`

const features = [
    { title: 'Integrations', subtitle: 'Automatically import tasks and events from other tools you use', icon: <Icon><FaBolt style={{color:'#e9c46a'}}/></Icon> },
    { title: 'Calendars', subtitle: 'Effectively manage and prioritize your day with time blocking', icon: <Icon><FaCalendarCheck style={{color:'#8ecae6'}}/></Icon> },
    { title: 'Unlimited tasks and pages', subtitle: 'No more 1000 tasks and 5 pages limit', icon: <Icon><FaClipboardCheck style={{color:'80ffdb'}}/></Icon> },
    // { title: 'Unlimited pages', subtitle: 'No more 5 pages limit', icon: <Icon><FaFileAlt style={{color:'eddcd2'}}/></Icon> },
    // { title: 'Collaboration', subtitle: 'Coming soon!', icon: <Icon><FaPeopleCarry style={{color:'#fad2e1'}}/></Icon> },
    // { title: '+ more', subtitle: '', icon: <Icon><FaCog/></Icon> },

]

const OverviewSection = props => {
    const {

    } = props;
    const dispatch = useDispatch()
    const user = useSelector((state) => state.auth.user)
    const plan = user['subscription_plan']
    const status = user['subscription_status']
    const subscription_end = user['subscription_end'] && format(new Date(user['subscription_end']), 'd MMM yyyy')
    const [frequency, setFrequency] = React.useState(user['subscription_frequency'] || 'YEARLY');
    const [stage, setStage] = React.useState(null);
    const [showDialogue, setDialogue] = React.useState(false)
    const [error, setError] = React.useState(false)
    const spinner = <Spinner styles={{color:'var(--text-color)'}}/>

    // React.useEffect(() => {
    //     dispatch(actionCreators.getWorkflows({ filters: { userId: uid } }))
    //     dispatch(actionCreators.getClients({ filters: { userId: uid } }))
    // }, [])

    //////////////
    // Handlers //
    //////////////
    const createCheckout = () => {
        setStage('in-progress')
        api.createCheckout({ userId: user['uid'], frequency: frequency.toLowerCase() })
        .then(res => {
            const url = res.data['url']
            window.open(url, '_self')
            setStage('completed')
        })
        .catch(err => { setStage(null) })
    }

    const closeDialogue = () => {
        setDialogue(false)
        setStage(null)
    }

    const updateSubscriptionStatus = () => {
        setStage('in-progress')
        api.updateSubscriptionStatus({ 
            userId: user['uid'], 
            status: plan === userContext.PLANS.PREMIUM & status === 'canceled' ? 'active'
                    : plan === userContext.PLANS.PREMIUM & ['active','past_due'].includes(status) ? 'canceled'
                    : plan === userContext.PLANS.PERSONAL & status === 'past_due' ? 'canceled'
                    : status === 'trialing' ? 'canceled'
                    : null
        })
        .then(res => {
            setTimeout(() => {
                setStage('completed')
                window.location.reload();
            }, 2000)
        })
        .catch(err => {
            console.log(err)
            setStage(null)
            setDialogue(false)
            setError('Changes not made, please try again')
        })
    }

    ////////////////
    // Components //
    ////////////////
    const planDescription = plan === userContext.PLANS.PERSONAL & status === 'past_due' ? 'Your Premium plan will be temporarily paused until the payment is resolved.'
                            : plan === userContext.PLANS.PERSONAL & status !== 'canceled' ? 'Start with a free 30 day trial! Upgrade to enjoy the full suite of features that we offer.'
                            : plan === userContext.PLANS.PREMIUM & status !== 'canceled' ? `Thanks for choosing us! Your plan will auto renew on ${subscription_end}`
                            : plan === userContext.PLANS.PREMIUM & status === 'canceled' ? `We're sad to see you go, your plan is slated to end on ${subscription_end}`
                            : status === 'canceled' ? "We're sad to see you go. We'll just be here, hoping that you'll come back "
                            : ''

    const featureBlocks = features.map(feature => (
        <FeatureTile>
            {feature['icon']}
            <Title>{feature['title']}</Title>
            <span/>
            <Subtitle styles={{color: 'var(--secondary-accent-color)'}}>{feature['subtitle']}</Subtitle>
        </FeatureTile>
    ))
    
    const subscriptionSpinner = !showDialogue & stage === 'in-progress' ? spinner 
                                : !showDialogue & stage === 'completed' ? <FaCheck/>
                                : null
    const cancelSpinner = stage === 'in-progress' ? spinner
                        : stage === 'completed' ? <FaCheck/>
                        : null

    return(
        <Container>
            <Header>
                <div style={{display: 'flex', flexDirection: 'column'}}>
                    <Title>Current plan: {plan === userContext.PLANS.PREMIUM ? 'Premium' : plan === userContext.PLANS.BETA ? 'Premium (Beta)' : 'Personal'}</Title>
                    <Subtitle>{planDescription}</Subtitle>
                </div>
            </Header>

            <Wrapper>
                <Body>
                    {featureBlocks}
                </Body>

                <SelectionContainer>
                    <SelectionTile selected={frequency === 'MONTHLY'} selectable={plan !== userContext.PLANS.PREMIUM}
                        onClick={() => plan !== userContext.PLANS.PREMIUM && setFrequency('MONTHLY')}>
                        <Subtitle>Pay monthly</Subtitle>
                        <Title>$4 per month</Title>
                    </SelectionTile>

                    <SelectionTile selected={frequency === 'YEARLY'} selectable={plan !== userContext.PLANS.PREMIUM}
                        onClick={() => plan !== userContext.PLANS.PREMIUM && setFrequency('YEARLY')}>
                        <div style={{display: 'flex', alignItems: 'center'}}>
                            <Subtitle>Pay yearly</Subtitle>
                            <Tag label='Save 25%' styles={{backgroundColor: 'var(--focus-color)', margin: '0 0.5rem', cursor: 'pointer'}}/>
                        </div>
                        <Title>$36 per year</Title>
                    </SelectionTile>

                    { plan === userContext.PLANS.BETA ? <Button text='Upgrade' styles={confirmButtonStyle} onSelect={() => dispatch(actionCreators.setModal(<BetaModal/>, 'betaModal'))}/>
                    : plan === userContext.PLANS.PERSONAL & status === 'past_due' ? <Button text='Cancel' onSelect={() => setDialogue(true)} styles={cancelButtonStyle}/>
                    : plan !== userContext.PLANS.PREMIUM ? <Button text={subscriptionSpinner || 'Upgrade'} styles={confirmButtonStyle} onSelect={createCheckout}/>
                    : plan === userContext.PLANS.PREMIUM & status === 'canceled' ? <Button text={subscriptionSpinner || 'Renew'} styles={confirmButtonStyle} onSelect={updateSubscriptionStatus}/>
                    : <Button text='Cancel' onSelect={() => setDialogue(true)} styles={cancelButtonStyle}/>}

                    {/* Error */}
                    {error && <Error>{error}</Error>}
                </SelectionContainer>
            </Wrapper>

            

            {/* Modal */}
            {showDialogue && 
            <Modal onClose={closeDialogue}>
                <DeleteDialogue closeHandler={closeDialogue} onDelete={updateSubscriptionStatus}
                    submitText={cancelSpinner || 'Confirm' }
                    warningText='Confirm cancelation?' subtitle={`Cancelation will take effect after the current cycle ends on ${subscription_end}`}
                    styles={{
                        boxShadow: 'var(--box-shadow)',
                        padding: '1rem',
                        border: '1px solid var(--primary-accent2-color)'
                    }}/>
            </Modal>}
        </Container>
    )
}

export default OverviewSection